import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ProfileFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileForm: React.FC<ProfileFormProps> = ({ isOpen, onClose }) => {
  const { state, actions } = useApp();
  const [name, setName] = useState(state.currentUser?.name || '');
  const [avatar, setAvatar] = useState(state.currentUser?.avatar || '👤');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Initialize form with saved draft when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    const savedDraft = sessionStorage.getItem('profileFormDraft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        setName(draftData.name || state.currentUser?.name || '');
        setAvatar(draftData.avatar || state.currentUser?.avatar || '👤');
      } catch (error) {
        console.error('Error loading profile draft:', error);
        // Keep current user data if draft is corrupted
        setName(state.currentUser?.name || '');
        setAvatar(state.currentUser?.avatar || '👤');
      }
    } else {
      // Reset to current user data if no draft
      setName(state.currentUser?.name || '');
      setAvatar(state.currentUser?.avatar || '👤');
    }
  }, [isOpen, state.currentUser]);

  // Auto-save draft to prevent data loss
  useEffect(() => {
    if (!isOpen) return;
    
    const hasChanges = name !== (state.currentUser?.name || '') || 
                      avatar !== (state.currentUser?.avatar || '👤');
    
    if (hasChanges && name.trim()) {
      sessionStorage.setItem('profileFormDraft', JSON.stringify({ name, avatar }));
    }
  }, [name, avatar, isOpen, state.currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await actions.updateProfile(name.trim(), avatar);
      
      // Clear draft on successful update
      sessionStorage.removeItem('profileFormDraft');
      
      setMessage('Profile updated successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const emojiOptions = ['👤', '😊', '🚀', '💡', '🔥', '⭐', '🎯', '💼', '🎨', '🔧', '📱', '💻'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Update Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field"
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Avatar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Avatar
            </label>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <span className="text-3xl">{avatar}</span>
                <span className="text-sm text-gray-600">Current avatar</span>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {emojiOptions.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => setAvatar(emoji)}
                    className={`p-2 text-2xl rounded-lg border-2 transition-colors ${
                      avatar === emoji 
                        ? 'border-deep-teal bg-deep-teal bg-opacity-10' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {message && (
            <div className="text-center p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700">{message}</p>
            </div>
          )}

          {error && (
            <div className="text-center p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !name.trim()}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileForm; 